// Racing-line operations — enable / disable / auto-generate / update.
//
// The racing line is an optional closed-loop spline. The AI follows it
// when present; otherwise the AI uses the centerline. Vertex-level
// edits (add / move / delete / sharp) reuse waypoint-ops via
// activeWaypoints(state) routing — same plumbing as trackline editing.

import { clearSelection } from './state.js';
import { pushHistory } from './history.js';
import { densifyClosedLoop } from '../../shared/spline.js';
import { seedRacingLineWaypoints } from '../../shared/racing-line.js';

// SUBS_CENTERLINE in shared/track-geometry.js. Kept in sync here so the
// auto-generate seed can sample the dense centerline at the same
// indices that align with the original centerline waypoints.
const CENTERLINE_SUBS = 12;

export function enableRacingLine(state, notify) {
  if (state.trackData.racingLine) return;
  state.trackData.racingLine = {
    waypoints: [],
    alpha:     0.5,
  };
  clearSelection(state);
  pushHistory(state, notify);
}

export function disableRacingLine(state, notify) {
  if (!state.trackData.racingLine) return;
  state.trackData.racingLine = null;
  clearSelection(state);
  pushHistory(state, notify);
}

// Auto-generate seed waypoints from the centerline + variable-width.
// Replaces any existing racing-line waypoints — this is intentional:
// the user clicked a button labeled "Auto-generate" expecting a fresh
// seed, not a merge. Undo restores prior state.
export function autoGenerateRacingLine(state, notify) {
  const td = state.trackData;
  const wps = td.trackline?.waypoints ?? [];
  if (wps.length < 4) return;
  const alpha = td.trackline.alpha ?? 0.5;
  const trackWidth = td.trackline.width ?? 16;

  // Densify the centerline + per-point half-widths the same way
  // shared/track-geometry does. Doing it inline (rather than pulling
  // from a cached geometry object) keeps this op decoupled from the
  // simulator's geometry build — the editor never invokes the full
  // build, so we'd be rebuilding it just for this call anyway.
  const dense = densifyClosedLoop(wps, CENTERLINE_SUBS, alpha);
  const halfWidths = computeHalfWidths(wps, trackWidth, alpha);

  const seed = seedRacingLineWaypoints(dense, halfWidths, CENTERLINE_SUBS);
  if (!seed || seed.length < 4) return;

  if (!td.racingLine) {
    td.racingLine = { waypoints: [], alpha: 0.5 };
  }
  td.racingLine.waypoints = seed;
  clearSelection(state);
  pushHistory(state, notify);
}

// Patch a property on the racing line (alpha). Caller picks live vs
// commit via the editor's applyChange / applyChangeLive pattern.
export function updateRacingLine(state, patch) {
  const rl = state.trackData.racingLine;
  if (!rl) return;
  Object.assign(rl, patch);
}

// Compute per-dense-point half-widths by linearly interpolating
// per-waypoint width overrides (wp.width) between consecutive
// waypoints. Mirrors shared/spline.js's widthAt logic but produces an
// array aligned to the dense polyline. Same shape that
// buildVariableWidthEdgesClosed returns in geometry.centerlineEdges.
function computeHalfWidths(waypoints, defaultWidth, _alpha) {
  const N = waypoints.length;
  if (N < 2) return [];
  const out = [];
  for (let i = 0; i < N; i++) {
    const w0 = waypoints[i].width ?? defaultWidth;
    const w1 = waypoints[(i + 1) % N].width ?? defaultWidth;
    for (let j = 0; j < CENTERLINE_SUBS; j++) {
      const t = j / CENTERLINE_SUBS;
      const w = w0 * (1 - t) + w1 * t;
      out.push(w / 2);
    }
  }
  return out;
}
